/**
 * CSV-Notfall-Import (Frage 9): Wenn die SumUp-API ausfällt, den Transaktionsexport aus dem SumUp-Dashboard
 * hochladen. Vier Schritte: Datei → Spalten prüfen → Vorschau → Import mit Abgleich.
 */
import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, Notice, Seitenzustand, StatusBadge } from "../ui";
import { Card, PageHeader, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatDatumZeit } from "@shared/zeit";
import { useCsvAnalyse, useCsvImport, useCsvVerlauf, useCsvVorschau, type CsvAnalyseAntwort, type CsvSpalten, type CsvVorschauAntwort } from "./verkaeufe-api";

const FELDER: { key: keyof CsvSpalten; label: string; pflicht: boolean }[] = [
  { key: "zeit", label: "Datum/Zeit", pflicht: true },
  { key: "betrag", label: "Betrag", pflicht: true },
  { key: "zahlungsart", label: "Zahlungsart", pflicht: false },
  { key: "transaktionsId", label: "Transaktions-ID", pflicht: false },
  { key: "status", label: "Status", pflicht: false },
  { key: "typ", label: "Typ", pflicht: false },
];

const ERGEBNIS: Record<string, { badge: "paid" | "wait" | "neutral" | "error"; text: string }> = {
  zugeordnet: { badge: "paid", text: "Passt zu Warenkorb" },
  neu: { badge: "wait", text: "Neu, ohne Warenkorb" },
  doppelt: { badge: "neutral", text: "Schon bekannt" },
  fehler: { badge: "error", text: "Fehler" },
};

export function CsvImport() {
  const analyse = useCsvAnalyse();
  const vorschau = useCsvVorschau();
  const importieren = useCsvImport();
  const verlauf = useCsvVerlauf();
  const datei = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [dateiname, setDateiname] = useState("");
  const [a, setA] = useState<CsvAnalyseAntwort | null>(null);
  const [spalten, setSpalten] = useState<CsvSpalten | null>(null);
  const [v, setV] = useState<CsvVorschauAntwort | null>(null);
  const [ergebnis, setErgebnis] = useState<{ neu: number; uebersprungen: number; zugeordnet: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const schritt = ergebnis ? 4 : v ? 3 : a ? 2 : 1;

  async function dateiLesen(f: File) {
    setFehler(null);
    setV(null);
    setErgebnis(null);
    if (f.size > 5_000_000) return setFehler("Die Datei ist größer als 5 MB.");
    const t = await f.text();
    setText(t);
    setDateiname(f.name);
    try {
      const r = await analyse.mutateAsync({ text: t, dateiname: f.name });
      setA(r);
      setSpalten(r.vorschlag);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Die Datei konnte nicht gelesen werden.");
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setZiehen(false);
    const f = e.dataTransfer.files[0];
    if (f) void dateiLesen(f);
  }

  function onWahl(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void dateiLesen(f);
    e.target.value = "";
  }

  async function vorschauLaden() {
    if (!spalten) return;
    setFehler(null);
    try {
      setV(await vorschau.mutateAsync({ text, spalten }));
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Vorschau hat nicht geklappt.");
    }
  }

  async function importStarten() {
    if (!spalten) return;
    setFehler(null);
    try {
      setErgebnis(await importieren.mutateAsync({ text, spalten, dateiname }));
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Import hat nicht geklappt.");
    }
  }

  function neuStarten() {
    setText("");
    setDateiname("");
    setA(null);
    setSpalten(null);
    setV(null);
    setErgebnis(null);
    setFehler(null);
  }

  const spaltenOk = !!spalten && !!spalten.zeit && !!spalten.betrag;

  return (
    <>
      <PageHeader kicker="Verkäufe und Abgleich" title="CSV-Notfall-Import" sub="Wenn SumUp nicht erreichbar ist: Transaktionsexport aus dem SumUp-Dashboard hochladen" actions={<Link to="/laden/verkaeufe" className="rg-btn rg-btn--quiet">Zurück zu Verkäufe</Link>} />
      <div className="sc-steps" style={{ marginBottom: "var(--space-4)" }}>
        {["Datei", "Spalten", "Vorschau", "Import"].map((s, i) => (
          <span key={s} className={["sc-step", i + 1 === schritt ? "is-on" : i + 1 < schritt ? "is-done" : ""].filter(Boolean).join(" ")}>
            <span className="sc-step__n">{i + 1}</span>
            {s}
          </span>
        ))}
      </div>
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}

      {schritt === 1 ? (
        <Card title="1 · Datei wählen">
          <p className="sc-muted">Im SumUp-Dashboard unter „Verkäufe“ den Zeitraum wählen und als CSV exportieren. Bereits bekannte Zahlungen werden beim Import übersprungen – doppelte Buchungen sind ausgeschlossen.</p>
          <div
            className="sc-drop"
            style={ziehen ? { borderColor: "var(--violett)", background: "var(--violett-soft)" } : undefined}
            onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
            onDragLeave={() => setZiehen(false)}
            onDrop={onDrop}
          >
            <Icon name="file-text" size={32} />
            <span className="sc-strong">CSV hierher ziehen</span>
            <span>oder</span>
            <Button variant="secondary" loading={analyse.isPending} onClick={() => datei.current?.click()}>
              Datei auswählen
            </Button>
            <input ref={datei} type="file" accept=".csv,text/csv,text/plain" hidden onChange={onWahl} />
          </div>
        </Card>
      ) : null}

      {schritt === 2 && a && spalten ? (
        <Card title="2 · Spalten prüfen" actions={<span className="sc-muted">{dateiname} · {a.anzahl} Zeilen · Trennzeichen „{a.trennzeichen === "\t" ? "Tab" : a.trennzeichen}“</span>}>
          <p className="sc-muted">Die Zuordnung wurde aus den Spaltennamen vorgeschlagen. Datum/Zeit und Betrag sind Pflicht.</p>
          <div className="sc-form">
            {FELDER.map((f) => (
              <Select key={f.key} label={`${f.label}${f.pflicht ? " *" : ""}`} value={spalten[f.key] ?? ""} onChange={(e) => setSpalten({ ...spalten, [f.key]: e.target.value || null })}>
                <option value="">{f.pflicht ? "– bitte wählen –" : "– nicht vorhanden –"}</option>
                {a.spalten.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            ))}
          </div>
          {a.beispiel.length ? (
            <div className="app-tabellen-scroll" style={{ marginTop: "var(--space-4)" }}>
              <table className="rg-table">
                <thead>
                  <tr>{a.spalten.map((s) => <th key={s}>{s}</th>)}</tr>
                </thead>
                <tbody>
                  {a.beispiel.map((z, i) => (
                    <tr key={i}>{a.spalten.map((s) => <td key={s}>{z[s]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <div className="app-form__actions" style={{ marginTop: "var(--space-4)" }}>
            <Button variant="primary" iconEnd="chevron-right" disabled={!spaltenOk} loading={vorschau.isPending} onClick={() => void vorschauLaden()}>
              Vorschau
            </Button>
            <Button variant="quiet" onClick={neuStarten}>Andere Datei</Button>
          </div>
        </Card>
      ) : null}

      {schritt === 3 && v ? (
        <Card
          title="3 · Vorschau"
          actions={
            <span className="sc-toolbar">
              <StatusBadge status="paid">{v.zusammenfassung.zugeordnet} passen</StatusBadge>
              <StatusBadge status="wait">{v.zusammenfassung.neu} neu</StatusBadge>
              <StatusBadge status="neutral">{v.zusammenfassung.doppelt} bekannt</StatusBadge>
              {v.zusammenfassung.fehler ? <StatusBadge status="error">{v.zusammenfassung.fehler} Fehler</StatusBadge> : null}
            </span>
          }
          flush
        >
          {v.zeilen.length === 0 ? (
            <EmptyState compact title="Keine Zahlungen in der Datei">Die Datei enthält keine auswertbaren Zeilen.</EmptyState>
          ) : (
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead>
                  <tr>
                    <th>Zeile</th>
                    <th>Zeit</th>
                    <th>Betrag</th>
                    <th>Zahlungsart</th>
                    <th>ID</th>
                    <th>Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  {v.zeilen.map((z) => {
                    const e = ERGEBNIS[z.ergebnis] ?? ERGEBNIS.fehler!;
                    return (
                      <tr key={z.zeile} className={z.ergebnis === "fehler" ? "app-zeile--fehler" : z.ergebnis === "zugeordnet" ? "app-zeile--ok" : undefined}>
                        <td className="app-code">{z.zeile}</td>
                        <td>{z.zeitpunkt ? formatDatumZeit(z.zeitpunkt) : "–"}</td>
                        <td className="sc-strong">{z.fehler ? "–" : formatEuro(z.betragCent)}</td>
                        <td>{z.zahlungsart === "bar" ? "Bar" : z.zahlungsart === "karte" ? "Karte" : "Sonstig"}{z.typ === "erstattung" ? " · Erstattung" : ""}</td>
                        <td className="app-code">{z.transactionCode}</td>
                        <td>
                          <StatusBadge status={e.badge}>{e.text}{z.warenkorbNummer ? ` #${z.warenkorbNummer}` : ""}</StatusBadge>
                          {z.fehler ? <div className="sc-muted sc-small">{z.fehler}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="app-form__actions" style={{ padding: "var(--space-4)" }}>
            <Button variant="primary" icon="check" disabled={v.zusammenfassung.neu + v.zusammenfassung.zugeordnet === 0} loading={importieren.isPending} onClick={() => void importStarten()}>
              {v.zusammenfassung.neu + v.zusammenfassung.zugeordnet} Zahlungen importieren
            </Button>
            <Button variant="quiet" onClick={() => setV(null)}>Zurück zu den Spalten</Button>
          </div>
        </Card>
      ) : null}

      {schritt === 4 && ergebnis ? (
        <Card title="4 · Import abgeschlossen">
          <Notice tone="success" title={`${ergebnis.neu} neue Zahlungen gespeichert`}>
            {ergebnis.zugeordnet} Warenkörbe wurden dadurch bezahlt · {ergebnis.uebersprungen} Zeilen waren schon bekannt und wurden übersprungen. Zahlungen ohne Warenkorb stehen unter „Nicht zugeordnet“.
          </Notice>
          <div className="app-form__actions" style={{ marginTop: "var(--space-4)" }}>
            <Link to="/laden/verkaeufe?tab=offen" className="rg-btn rg-btn--primary">Nicht zugeordnete prüfen</Link>
            <Button variant="quiet" onClick={neuStarten}>Weitere Datei</Button>
          </div>
        </Card>
      ) : null}

      <Seitenzustand query={verlauf} lines={2}>
        {(d) => d.importe.length === 0 ? null : (
          <Card title="Bisherige Importe" flush>
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Datei</th>
                    <th>Zeilen</th>
                    <th>Neu</th>
                    <th>Zugeordnet</th>
                    <th>Übersprungen</th>
                  </tr>
                </thead>
                <tbody>
                  {[...d.importe].reverse().map((i) => (
                    <tr key={i.id}>
                      <td>{formatDatumZeit(i.erstelltAt)}</td>
                      <td>{i.dateiname}</td>
                      <td>{i.zeilen}</td>
                      <td>{i.neu}</td>
                      <td>{i.zugeordnet}</td>
                      <td>{i.uebersprungen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Seitenzustand>
    </>
  );
}
